/* global globalThis */
/**
 * @fileoverview Member Quickview Component
 * @module components/members/MemberQuickview
 * @description A comprehensive member quickview panel that displays member details,
 * opportunities, and notes in a modal overlay.
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material/Close
 * @requires ../cards/NotesCard
 * @requires ../opportunities/TopOpportunities
 * @requires ../../services/mockDataService
 * @requires ../../api/quickview
 * @requires ../../api/priority
 * @requires ../../api/oppurtunity
 * @requires ../../api/api
 */

import { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotesCard from "../cards/NotesCard";
import { styled, useTheme } from "@mui/material/styles";
import TopPharmacyOpportunities from "../opportunities/topPharmacyOppurtunities";
import api from "../../api/api";
import {
  fetchPharmacyOverview,
  fetchProviderOverview,
} from "../../api/provider";
import {
  fetchPharmacyOpportunities,
  fetchProviderOpportunities,
} from "../../api/oppurtunity";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { useLoading } from "../../context/LoadingContext";

/**
 * Parses a drug name string to extract the base name and strength
 * @param {string} drugName - The full drug name string to parse
 * @returns {Object} An object containing the parsed name and strength
 * @property {string} name - The base name of the drug
 * @property {string} strength - The extracted strength information
 */

/**
 * Styled component for the modal overlay
 * @constant {Object} Overlay
 * @description Creates a semi-transparent overlay that covers the entire viewport
 */ const Overlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1200,
  overflow: "hidden",
});

/**
 * Styled component for the main popup container
 * @constant {Object} PopupContainer
 * @description Creates a responsive popup container with slide-in animation
 */
const PopupContainer = styled(Box)(({ theme }) => ({
  width: "90vw",
  maxWidth: "1250px",
  maxHeight: "90vh", // 👈 key for responsiveness
  minHeight: "400px",
  backgroundColor: "#F7F8FB",
  borderRadius: "16px",
  boxShadow: "4px 0 20px rgba(0, 0, 0, 0.15)",
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-100%, -50%)",
  animation: "slideIn 0.3s ease-out forwards",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1300,

  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    maxHeight: "95vh",
  },

  "@keyframes slideIn": {
    from: { transform: "translate(-100%, -50%)" },
    to: { transform: "translate(-50%, -50%)" },
  },
}));

/**
 * Styled component for the inner content wrapper
 * @constant {Object} ContentWrapper
 * @description Wraps the main content area with proper spacing and scrolling
 */
const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "20px 24px 24px",
  gap: "16px",
  backgroundColor: "#F7F8FB",
  overflowY: "auto", // 👈 add this
});

/**
 * MemberQuickview Component
 *
 * Displays a detailed view of member information including opportunities, notes, and program status.
 * Can be used in different contexts based on the source prop.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback function to close the quickview
 * @param {Object} props.memberData - Member data object containing member information
 * @param {Function} props.onNoteUpdate - Callback function when notes are updated
 * @param {string} [props.source='memberMonitoring'] - The source context for the quickview ('memberMonitoring' or 'priorityOutreach')
 * @returns {JSX.Element} The rendered MemberQuickview component
 */
const MemberQuickview = ({
  onClose,
  memberData: propMemberData,
  onNoteUpdate,
  _source = "memberMonitoring",
}) => {
  const [selectedNoteType, setSelectedNoteType] = useState([]);
  const [memberData, setMemberData] = useState(propMemberData.member || null);
  const [notesSearchQuery, setNotesSearchQuery] = useState("");
  const [opportunities, setOpportunities] = useState([]);

  const [notes, setNotes] = useState([]);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [loading, setLoading] = useState({
    member: true,
    opportunities: true,
    notes: true,
  });
  const [error, setError] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  const fetchMemberOpportunities = useCallback(
    async (memberId, pharmacyOrProviderId) => {
      try {
        showLoading();
        let response;
        if (propMemberData.type === "provider") {
          response = await fetchProviderOpportunities(
            memberId,
            pharmacyOrProviderId,
          );
        } else {
          response = await fetchPharmacyOpportunities(
            memberId,
            pharmacyOrProviderId,
          );
        }

        let opportunitiesData = [];
        if (Array.isArray(response)) {
          opportunitiesData = response;
        } else if (response?.content) {
          opportunitiesData = response.content;
        } else if (response?.data) {
          opportunitiesData = response.data;
        }

        if (!opportunitiesData?.length) {
          if (isMounted.current) {
            setOpportunities([]);
            setLoading((prev) => ({ ...prev, opportunities: false }));
          }
          return;
        }

        const sortedOpportunities = [...opportunitiesData].sort((a, b) => {
          const timeA = a.recentActivity?.timestamp
            ? new Date(a.recentActivity.timestamp)
            : new Date(0);
          const timeB = b.recentActivity?.timestamp
            ? new Date(b.recentActivity.timestamp)
            : new Date(0);
          return timeB - timeA;
        });

        const topOpportunities = sortedOpportunities.map((item) => {
          const member = item.member || {};
          const manualTask = item.manualTask || {};

          return {
            id: item.id,
            timezone: member.timezone || "N/A",
            language: member.language || "N/A",
            name: capitalizeFirstLetter(member?.fullName || "N/A"),
            outreachReason: manualTask.outreachReason,
            dueDate: manualTask.dueDate,
          };
        });

        if (isMounted.current) {
          setOpportunities(topOpportunities);
          setLoading((prev) => ({ ...prev, opportunities: false }));
        }
      } catch (err) {
        console.error("Error fetching opportunities:", err);
        if (isMounted.current) {
          setError("Failed to load opportunities. Please try again.");
          setOpportunities([]);
        }

        hideLoading();
      } finally {
        hideLoading();
      }
    },
    [propMemberData?.type, showLoading, hideLoading],
  );

  const fetchNotes = async (
    memberId,
    searchQuery = "",
    entityType = "MEMBER",
  ) => {
    try {
      const params = new URLSearchParams({
        entityType,
        entityId: memberId,
      });

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("searchQuery", searchQuery.trim());
      }

      const url = `/api/notes?${params.toString()}`;

      const response = await api.get(url, {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      });

      let notesData = [];
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        notesData = [...responseData];
      } else if (Array.isArray(responseData?.content)) {
        notesData = [...responseData.content];
      } else if (Array.isArray(responseData?.latest)) {
        notesData = [...responseData.latest];
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        notesData = [...responseData.data];
      } else if (responseData && typeof responseData === "object") {
        notesData = [responseData];
      }

      notesData = notesData.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      );

      const formattedNotes = notesData.map((note) => {
        let authorName = "System";
        if (note.author) {
          authorName =
            typeof note.author === "number"
              ? note.author.toString()
              : note.author;
        } else if (note.createdBy) {
          authorName =
            typeof note.createdBy === "number"
              ? note.createdBy.toString()
              : note.createdBy;
        }

        const noteType = (() => {
          const types = [];
          if (note.noteType) types.push(note.noteType);
          if (note.type) types.push(note.type);

          return [...new Set(types)][0] || "General";
        })();

        return {
          ...note,

          content: note.noteEntry || note.content || "",
          type: noteType,
          author: authorName,

          createdAt: note.createdAt,
          updateAt: note.updateAt || note.updatedAt || null,
          updatedAt: note.updateAt || note.updatedAt || null,

          pinNote: note.pinned || false,
          id: note.noteId || note.id,
          rating: note.rating,
        };
      });
      const filteredFormattedNotes = formattedNotes.filter(
        (note) => note.content?.trim() !== "",
      );

      if (isMounted.current) {
        setNotes(filteredFormattedNotes);
        setLoading((prev) => ({ ...prev, notes: false }));
      }
    } catch (err) {
      if (isMounted.current) {
        setError(`Error loading notes: ${err.message}`);
        setNotes([]);
        setLoading((prev) => ({ ...prev, notes: false }));
      }
    }
  };

  const handleNotesSearch = (query) => {
    setNotesSearchQuery(query);

    const memberId = propMemberData?.memberId || propMemberData?.id;
    if (memberId) {
      void fetchNotes(
        memberId,
        query,
        propMemberData?.type === "provider" ? "PROVIDER" : "PHARMACY",
      );
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fetchQuickViewData = useCallback(async () => {
    if (!propMemberData?.memberId && !propMemberData?.id) {
      if (isMounted.current) {
        setError("No member ID provided");
        setLoading({ member: false, opportunities: false, notes: false });
      }
      return;
    }

    const memberId = propMemberData.memberId || propMemberData.id;
    const pharmacyOrProviderId =
      propMemberData.memberDataId ||
      propMemberData.pharmacyId ||
      propMemberData.providerId;

    if (!memberId) return;

    setError(null);

    try {
      let memberOverview;
      if (propMemberData.type === "provider") {
        memberOverview = await fetchProviderOverview(memberId);
      } else {
        memberOverview = await fetchPharmacyOverview(memberId);
      }

      await Promise.all([
        fetchMemberOpportunities(memberId, pharmacyOrProviderId),
        fetchNotes(
          memberId,
          "",
          propMemberData?.type === "provider" ? "PROVIDER" : "PHARMACY",
        ),
      ]);

      const locations = [];

      const contact = memberOverview.contact || {};
      const address1 = memberOverview.address || {};
      locations.push({
        phone: contact.phone ?? "N/A",
        email: contact.email ?? "N/A",
        fax: contact.fax ?? memberOverview.faxNumber ?? "N/A",
        address: {
          street: address1.street ?? "",
          address2: address1.address2 ?? "",
          city: address1.city ?? "",
          state: address1.state ?? "",
          pinCode: address1.zip ?? "",
          province: "",
        },
      });

      const loc2 = memberOverview.location2 || {};
      const address2 = loc2.address || {};
      locations.push({
        phone: loc2.phoneNumber ?? "N/A",
        email: loc2.email ?? "N/A",
        fax: loc2.faxNumber ?? "N/A",
        address: {
          street: address2.street ?? "",
          address2: address2.address2 ?? "",
          city: address2.city ?? "",
          state: address2.state ?? "",
          pinCode: address2.zip ?? "",
          province: "",
        },
      });

      const cleanData = {
        ...propMemberData,
        ...memberOverview,
        name: memberOverview?.name || propMemberData?.name || "",
        gender: (() => {
          const gender = memberOverview?.gender || propMemberData?.gender || "";
          return gender &&
            !["unknown", "undefined", ""].includes(gender.toLowerCase())
            ? gender
            : "N/A";
        })(),
        dateOfBirth:
          memberOverview?.dateOfBirth ||
          memberOverview?.dob ||
          propMemberData?.dateOfBirth ||
          propMemberData?.dob ||
          "",
        locations,
        contact: {
          phone: contact.phone ?? "N/A",
          email: contact.email ?? "N/A",
          fax: contact.fax ?? memberOverview.faxNumber ?? "N/A",
          language:
            memberOverview?.language ||
            contact.language ||
            propMemberData?.contact?.language ||
            propMemberData?.language ||
            "N/A",
          timeZone:
            memberOverview?.timeZone ||
            contact.timeZone ||
            propMemberData?.contact?.timeZone ||
            propMemberData?.timeZone ||
            "N/A",
          address:
            (typeof address1.fullAddress === "string" &&
              address1.fullAddress) ||
            contact.address ||
            propMemberData?.contact?.address ||
            "",
        },
        memberId: memberId,
        id: memberId,
      };

      if (isMounted.current) {
        setMemberData(cleanData);
        setLoading((prev) => ({ ...prev, member: false }));
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "Failed to load member data");
        setLoading((prev) => ({ ...prev, member: false }));
      }
      hideLoading();
    } finally {
      hideLoading();
    }
  }, [propMemberData, fetchMemberOpportunities, hideLoading]);

  useEffect(() => {
    if (!propMemberData) {
      setLoading({ member: false, opportunities: false, notes: false });
      setError("No member data provided");
      return;
    }

    const memberId = propMemberData.memberId || propMemberData.id;
    const cleanData = {
      memberId: memberId || "",
      id: memberId || "",
      name: propMemberData.name || propMemberData.member?.name || "",
      language:
        propMemberData.language || propMemberData.contact?.language || "",
      timeZone:
        propMemberData.timeZone || propMemberData.contact?.timeZone || "",
      gender: (() => {
        const genderValue = propMemberData?.sex || propMemberData?.gender;

        const contactGender =
          propMemberData?.contact?.sex || propMemberData?.contact?.gender;

        const finalValue = genderValue || contactGender;

        if (
          !finalValue ||
          String(finalValue).trim() === "" ||
          ["unknown", "undefined"].includes(String(finalValue).toLowerCase())
        ) {
          return "N/A";
        }

        return (
          String(finalValue).charAt(0).toUpperCase() +
          String(finalValue).slice(1).toLowerCase()
        );
      })(),
      age: propMemberData.age || "",
      dob: propMemberData.dob || propMemberData.dateOfBirth || "",
      dateOfBirth: propMemberData.dateOfBirth || propMemberData.dob || "",
      knownAllergies: Array.isArray(propMemberData.knownAllergies)
        ? propMemberData.knownAllergies
        : [],
      allergies: Array.isArray(propMemberData.allergies)
        ? propMemberData.allergies
        : [],
      status: propMemberData.status || "",
      contact: {
        phone: propMemberData.contact?.phone || "",
        email:
          propMemberData.contact?.email &&
          !propMemberData.contact.email.includes("undefined")
            ? propMemberData.contact.email
            : "N/A",
        language:
          propMemberData.contact?.language || propMemberData.language || "",
        timeZone:
          propMemberData.contact?.timeZone || propMemberData.timeZone || "",
        address: propMemberData.contact?.address || "",
      },
    };

    setMemberData(cleanData);

    if (memberId) {
      showLoading();
      setLoading({
        member: true,
        opportunities: true,
        notes: true,
      });

      fetchQuickViewData();
    } else {
      setLoading({ member: false, opportunities: false, notes: false });
    }
  }, [
    propMemberData,
    propMemberData?.memberId,
    propMemberData?.id,
    fetchQuickViewData,
    showLoading,
  ]);

  useEffect(() => {
    const originalStyle = globalThis.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    globalThis.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = originalStyle;
      globalThis.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <Overlay onClick={onClose}>
      <PopupContainer
        component="section"
        aria-label="Pharmacy quickview"
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            borderRadius: "16px",
            overflow: "visible",
            backgroundColor: "#ECECF1",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            zIndex: 1600,
            maxHeight: "inherit",
          }}
        >
          <ContentWrapper>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "20px !important",
                  color: "#403C6F",
                }}
              >
                {propMemberData?.type === "provider"
                  ? "Provider Details"
                  : "Pharmacy Details"}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "#403C6F",
                  marginLeft: "16px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "16px",
                backgroundColor: "white",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 600, color: "#403C6F" }}
                >
                  {memberData?.name}
                </Typography>
                <Chip
                  label={memberData?.status || ""}
                  size="small"
                  sx={{
                    bgcolor: "#E8F5E9",
                    color: "#1B5E20",
                    fontWeight: 600,
                    fontSize: "12px",
                    borderRadius: "12px",
                    height: "24px",
                    border: "1px solid #45B184",
                    boxSizing: "border-box",
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  "& > span": { marginRight: "12px" },
                  "& .label": { marginRight: "4px" },
                }}
              >
                <span>
                  <Box
                    component="span"
                    className="label"
                    sx={{
                      color: "#2D2D2D",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {propMemberData?.type === "provider" ? "NPI:" : "NABP:"}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: "#403C6F",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    {propMemberData?.type === "provider"
                      ? memberData?.npi || "N/A"
                      : memberData?.nabp || "N/A"}
                  </Box>
                </span>
                {propMemberData?.type === "provider" && (
                  <span>
                    <Box
                      component="span"
                      className="label"
                      sx={{
                        color: "#2D2D2D",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                      }}
                    >
                      DEA:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: "#403C6F",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                      }}
                    >
                      {memberData?.dea || "N/A"}
                    </Box>
                  </span>
                )}
              </Typography>

              {Array.isArray(memberData?.locations) &&
                memberData.locations.map((loc, idx) => (
                  <Box
                    key={`location-${loc.phone || ""}-${loc.email || ""}-${loc.address || ""}-${idx}`}
                    sx={{ mb: 0 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        "& > span": { marginRight: "12px" },
                        "& .label": { marginRight: "4px" },
                      }}
                    >
                      <Box
                        component="span"
                        className="label"
                        sx={{
                          color: "#403C6F",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                        }}
                      >
                        {`Location ${idx + 1} :`}
                      </Box>
                      <span>
                        <Box
                          component="span"
                          className="label"
                          sx={{
                            color: "#2D2D2D",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Phone Number
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                          }}
                        >
                          {loc.phone && loc.phone !== null && loc.phone !== ""
                            ? loc.phone
                            : "N/A"}
                        </Box>
                      </span>
                      <span>
                        <Box
                          component="span"
                          className="label"
                          sx={{
                            color: "#2D2D2D",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Mail Id
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                          }}
                        >
                          {loc.email && loc.email !== null && loc.email !== ""
                            ? loc.email
                            : "N/A"}
                        </Box>
                      </span>
                      <span>
                        <Box
                          component="span"
                          className="label"
                          sx={{
                            color: "#2D2D2D",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Fax Number
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                          }}
                        >
                          {loc.fax && loc.fax !== null && loc.fax !== ""
                            ? loc.fax
                            : "N/A"}
                        </Box>
                      </span>
                      <span>
                        <Box
                          component="span"
                          className="label"
                          sx={{
                            color: "#2D2D2D",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                          }}
                        >
                          Address
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#403C6F",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                          }}
                        >
                          {(() => {
                            const a = loc.address || {};

                            const parts = [
                              a.street,
                              a.address2,
                              a.city,
                              a.state,
                              a.province,
                              a.pinCode,
                            ].filter(
                              (v) => v && v !== null && v !== "" && v !== "N/A",
                            );
                            return parts.length > 0 ? parts.join(", ") : "";
                          })()}
                        </Box>
                      </span>
                    </Typography>
                  </Box>
                ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "16px",
                width: "100%",
                flex: 1,
                minHeight: { xs: "400px", md: "495px" },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderRadius: "18px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "white",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                  p: "20px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minHeight: "300px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "150%",
                    letterSpacing: "0%",
                    color: "#403C6F",
                  }}
                >
                  Top Opportunities
                </Typography>
                <Divider />
                <TopPharmacyOpportunities
                  opportunities={opportunities}
                  source={_source}
                  type={propMemberData.type}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  borderRadius: "18px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "white",
                  fontWeight: 600,
                  fontSize: "16px",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: isMobile ? "" : "hidden",
                  minHeight: "400px",
                  "& .MuiBox-root": {
                    overflow: "visible !important",
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {(() => {
                    if (loading.notes) {
                      return (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                        >
                          <CircularProgress size={24} />
                        </Box>
                      );
                    } else if (error) {
                      return (
                        <Box p={2} color="error.main">
                          {error}
                        </Box>
                      );
                    } else {
                      return (
                        <NotesCard
                          notes={notes}
                          selectedNoteType={selectedNoteType}
                          searchQuery={notesSearchQuery}
                          onNoteTypeChange={setSelectedNoteType}
                          onSearch={handleNotesSearch}
                          onTogglePin={(noteId) => {
                            const updatedNotes = notes.map((note) =>
                              note.id === noteId
                                ? { ...note, pinned: !note.pinned }
                                : note,
                            );
                            setNotes(updatedNotes);
                            if (onNoteUpdate) onNoteUpdate(updatedNotes);
                          }}
                          inQuickview={true}
                        />
                      );
                    }
                  })()}
                </Box>
              </Box>
            </Box>
          </ContentWrapper>
        </Box>
      </PopupContainer>
    </Overlay>
  );
};

MemberQuickview.propTypes = {
  onClose: PropTypes.func.isRequired,
  memberData: PropTypes.shape({
    member: PropTypes.object,
    memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    gender: PropTypes.string,
    dateOfBirth: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    dob: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.oneOf(["provider", "pharmacy"]),
    pharmacyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    providerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    memberDataId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    contact: PropTypes.shape({
      phone: PropTypes.string,
      email: PropTypes.string,
      fax: PropTypes.string,
      language: PropTypes.string,
      timeZone: PropTypes.string,
      address: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      sex: PropTypes.string,
      gender: PropTypes.string,
    }),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        phone: PropTypes.string,
        email: PropTypes.string,
        fax: PropTypes.string,
        address: PropTypes.shape({
          street: PropTypes.string,
          address2: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          province: PropTypes.string,
          pinCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      }),
    ),
    language: PropTypes.string,
    timeZone: PropTypes.string,
    sex: PropTypes.string,
    knownAllergies: PropTypes.array,
    allergies: PropTypes.array,
  }).isRequired,
  onNoteUpdate: PropTypes.func,
  _source: PropTypes.oneOf(["memberMonitoring", "priorityOutreach"]),
};

export default MemberQuickview;
